import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Plus, Search, ChevronLeft, ChevronRight, Ban, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function CustomerMasterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(1);
  const limit = 25;

  useEffect(() => { loadCustomers(); }, [page, statusFilter]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await contractsAPI.listCustomers(params);
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadCustomers(); };

  const handleSuspend = async (customer) => {
    const action = customer.status === 'active' ? 'suspend' : 'activate';
    if (!window.confirm(`${action === 'suspend' ? 'Suspend' : 'Activate'} ${customer.customer_name}?`)) return;
    try {
      await contractsAPI.suspendCustomer(customer.id);
      toast.success(`Customer ${action}d`);
      loadCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4" data-testid="customer-master-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Customer Master</h1>
          <p className="text-xs text-muted-foreground">{total} customers</p>
        </div>
        <Button size="sm" onClick={() => navigate('/contracts/customers/new')} data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Customer
        </Button>
      </div>

      <Card>
        <CardContent className="p-3 flex gap-2 flex-wrap">
          <div className="flex gap-1 flex-1 min-w-[200px]">
            <Input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="h-8 text-xs" data-testid="customer-search" />
            <Button size="sm" variant="outline" className="h-8" onClick={handleSearch}><Search className="w-3.5 h-3.5" /></Button>
          </div>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="customer-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Code</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Customer Name</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Contact</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">City</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">GST</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Status</th>
                <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No customers found</td></tr>
              ) : customers.map(c => (
                <tr key={c.id} className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/contracts/customers/${c.id}`)}
                  data-testid={`customer-row-${c.customer_code}`}>
                  <td className="p-2.5 font-mono text-xs font-medium">{c.customer_code}</td>
                  <td className="p-2.5 text-xs font-medium">{c.customer_name}</td>
                  <td className="p-2.5 text-xs text-muted-foreground">{c.contact_person || c.email || '-'}</td>
                  <td className="p-2.5 text-xs text-muted-foreground">{c.city || '-'}</td>
                  <td className="p-2.5 text-xs font-mono text-muted-foreground">{c.gst_number || '-'}</td>
                  <td className="p-2.5">
                    <Badge variant="outline" className={`text-[10px] ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      {c.status}
                    </Badge>
                  </td>
                  <td className="p-2.5 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => navigate(`/contracts/customers/${c.id}/edit`)} data-testid={`edit-customer-${c.customer_code}`}>
                        Edit
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant={c.status === 'active' ? 'destructive' : 'default'}
                          className="h-6 text-[10px]" onClick={() => handleSuspend(c)} data-testid={`suspend-customer-${c.customer_code}`}>
                          {c.status === 'active' ? <><Ban className="w-3 h-3 mr-0.5" /> Suspend</> : <><CheckCircle className="w-3 h-3 mr-0.5" /> Activate</>}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
